import { useState } from "react";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";
import { Play, Trash2, FlaskConical, Zap } from "lucide-react";
import { useI18n } from "@/context/I18nContext";

import { BACKEND_URL } from '../../utils/apiBase';
const API = `${BACKEND_URL}/api`;

const SPEED_OPTIONS = [
  { id: "instant", label: "Instant", delayMs: 0 },
  { id: "10x", label: "10×", delayMs: 150 },
  { id: "5x", label: "5×", delayMs: 400 },
  { id: "1x", label: "1× (réel)", delayMs: 1500 },
];

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Orchestration côté client : start -> auto-vote -> reveal-all -> finish.
 * Cette fonction réutilise les endpoints existants pour rester compatible
 * avec le timer et les WebSockets.
 */
async function simulateBattle(battleId, delayMs) {
  // Battle may already be voting (current battle); only start if pending
  try {
    const res = await axios.get(`${API}/battle/${battleId}/votes`);
    void res; // noop - just to make sure axios works
  } catch {
    /* ignore */
  }
  // Use a single-call sequence; status checks happen server-side.
  try {
    await axios.post(`${API}/battle/${battleId}/start`);
  } catch (e) {
    // Already voting/revealing is fine; rethrow only on hard errors
    if (e?.response?.status && e.response.status !== 409 && e.response.status !== 400) {
      throw e;
    }
  }
  await wait(delayMs);
  await axios.post(`${API}/dry-run/battle/${battleId}/auto-vote`);
  await wait(delayMs);
  await axios.post(`${API}/battle/${battleId}/reveal-all`);
  await wait(delayMs);
  await axios.post(`${API}/battle/${battleId}/finish`);
}

async function simulateCircle(circleId, delayMs) {
  try {
    await axios.post(`${API}/circle/${circleId}/start`);
  } catch (e) {
    if (e?.response?.status && e.response.status !== 409 && e.response.status !== 400) {
      throw e;
    }
  }
  await wait(delayMs);
  await axios.post(`${API}/dry-run/circle/${circleId}/auto-vote`);
  await wait(delayMs);
  await axios.post(`${API}/circle/${circleId}/reveal-all`);
  await wait(delayMs);
  await axios.post(`${API}/circle/${circleId}/finish`);
}

export const DryRunPanel = ({
  simulationMode,
  eventId,
  currentBattle,
  currentCircle,
  upcomingSections,
  onAfterRun,
}) => {
  const { t } = useI18n();
  const [running, setRunning] = useState(false);
  const [speedIdx, setSpeedIdx] = useState(0); // default Instant
  const speed = SPEED_OPTIONS[speedIdx];

  if (!simulationMode) return null;

  const totalUpcoming = (upcomingSections || []).reduce(
    (acc, s) => acc + (s.battles?.length || 0) + (s.circles?.length || 0),
    0
  );

  const runCurrent = async () => {
    setRunning(true);
    try {
      if (currentBattle?.battle?.id) {
        await simulateBattle(currentBattle.battle.id, speed.delayMs);
      } else if (currentCircle?.circle?.id) {
        await simulateCircle(currentCircle.circle.id, speed.delayMs);
      } else {
        toast.error(t("dryRun.noActiveEvent"));
        return;
      }
      toast.success(t("dryRun.currentDone"));
      onAfterRun && onAfterRun();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("dryRun.error"));
    } finally {
      setRunning(false);
    }
  };

  const runAll = async () => {
    if (!confirm(t("dryRun.confirmRunAll"))) return;
    setRunning(true);
    try {
      // First, finish current event if any
      if (currentBattle?.battle?.id) {
        await simulateBattle(currentBattle.battle.id, speed.delayMs);
        await wait(speed.delayMs);
      } else if (currentCircle?.circle?.id) {
        await simulateCircle(currentCircle.circle.id, speed.delayMs);
        await wait(speed.delayMs);
      }
      // Then iterate upcoming sections in their current order. We refetch each
      // pass so newly-populated battles (next round winners) are picked up.
      let safety = 200;
      while (safety-- > 0) {
        const res = await axios.get(`${API}/upcoming-events`);
        const sections = res.data || [];
        let next = null;
        for (const s of sections) {
          const c = (s.circles || []).find(
            (c) => c.status === "pending" && (c.athlete_ids || []).filter(Boolean).length >= 2
          );
          if (c) {
            next = { type: "circle", id: c.id };
            break;
          }
          const b = (s.battles || []).find(
            (b) => b.status === "pending" && b.athlete1_id && b.athlete2_id
          );
          if (b) {
            next = { type: "battle", id: b.id };
            break;
          }
        }
        if (!next) break;
        if (next.type === "battle") {
          await simulateBattle(next.id, speed.delayMs);
        } else {
          await simulateCircle(next.id, speed.delayMs);
        }
        await wait(speed.delayMs);
      }
      toast.success(t("dryRun.allDone"));
      onAfterRun && onAfterRun();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("dryRun.error"));
    } finally {
      setRunning(false);
    }
  };

  const resetSimulated = async () => {
    if (!confirm(t("dryRun.confirmReset"))) return;
    setRunning(true);
    try {
      const res = await axios.delete(`${API}/dry-run/reset`);
      toast.success(
        t("dryRun.resetDone")
          .replace("{battles}", res.data.reset_battles || 0)
          .replace("{circles}", res.data.reset_circles || 0)
      );
      onAfterRun && onAfterRun();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("dryRun.error"));
    } finally {
      setRunning(false);
    }
  };

  return (
    <Card
      className="p-6 mb-6 border-2"
      style={{
        borderColor: "#f97316",
        background:
          "linear-gradient(135deg, rgba(249,115,22,0.10), rgba(249,115,22,0.04))",
      }}
      data-testid="dry-run-panel"
    >
      <div className="flex items-center gap-3 mb-4">
        <FlaskConical className="text-orange-500" size={28} />
        <h3 className="text-2xl font-bold text-orange-500">
          {t("dryRun.title")}
        </h3>
        <span
          className="ml-auto px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-orange-500 text-white"
          data-testid="dry-run-active-badge"
        >
          {t("dryRun.active")}
        </span>
      </div>
      <p className="text-sm text-muted-foreground mb-4">
        {t("dryRun.description")}
      </p>

      <div className="mb-4">
        <label className="text-sm font-medium mb-2 block">
          {t("dryRun.speed")}: <strong>{speed.label}</strong>
        </label>
        <Slider
          min={0}
          max={SPEED_OPTIONS.length - 1}
          step={1}
          value={[speedIdx]}
          onValueChange={(v) => setSpeedIdx(v[0])}
          data-testid="dry-run-speed-slider"
        />
        <div className="flex justify-between text-xs text-muted-foreground mt-1">
          {SPEED_OPTIONS.map((o) => (
            <span key={o.id}>{o.label}</span>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Button
          onClick={runCurrent}
          disabled={running || (!currentBattle && !currentCircle)}
          className="bg-orange-500 hover:bg-orange-600 text-white"
          data-testid="dry-run-run-current-btn"
        >
          <Play size={16} className="mr-2" /> {t("dryRun.runCurrent")}
        </Button>

        <Button
          onClick={runAll}
          disabled={running}
          className="bg-orange-600 hover:bg-orange-700 text-white"
          data-testid="dry-run-run-all-btn"
        >
          <Zap size={16} className="mr-2" />{" "}
          {t("dryRun.runAll").replace("{count}", totalUpcoming)}
        </Button>

        <Button
          onClick={resetSimulated}
          disabled={running}
          variant="outline"
          className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
          data-testid="dry-run-reset-btn"
        >
          <Trash2 size={16} className="mr-2" /> {t("dryRun.reset")}
        </Button>
      </div>

      {running && (
        <div
          className="mt-4 text-sm text-orange-500 font-medium"
          data-testid="dry-run-running-indicator"
        >
          {t("dryRun.running")}
        </div>
      )}
    </Card>
  );
};

export default DryRunPanel;
